<template>
  <div v-if="isSuggestionCard" class="flex flex-col gap-2">
    <SuggestionCard
      v-for="(id, index) in visibleSuggestionIds"
      :key="id"
      :suggestion-id="id"
      :rank="index + 1"
      @dismiss="onDismissSuggestion"
    />
  </div>
  <div v-else-if="isRemovalCard" class="flex flex-col gap-2">
    <RemovalSuggestionCard
      v-for="(id, index) in visibleRemovalIds"
      :key="id"
      :suggestion-id="id"
      :rank="index + 1"
      @dismiss="onDismissRemoval"
    />
  </div>
  <CommandButton v-else-if="isCommandButton" :action="action" />
  <button
    v-else
    role="button"
    class="inline-flex w-[150px] items-center justify-center rounded-[6px] bg-gpx_terracotta-300 px-3 py-1 text-gpx_button font-medium text-white hover:bg-gpx_terracotta-200"
    @click.prevent="handleAction"
  >
    {{ label }}
  </button>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toastification';
import {
  declineAccountPlaceSuggestion,
  declineAccountGeofenceRemovalSuggestion,
} from '@gpx-spa/queries';
import * as events from '@/events';
import CommandButton from './CommandButton.vue';
import SuggestionCard from '@/components/place-suggestions/SuggestionCard.vue';
import RemovalSuggestionCard from '@/components/geofence-removals/RemovalSuggestionCard.vue';

const toast = useToast();
const store = useStore();

const props = defineProps({
  action: {
    type: Object,
    required: true,
  },
});

const isSuggestionCard = computed(
  () => props.action.name === 'ui_suggestion_card',
);
const isRemovalCard = computed(() => props.action.name === 'ui_removal_card');

const suggestionIds = computed(() => {
  const ids = props.action.arguments?.suggestion_ids;
  return Array.isArray(ids) ? ids : [];
});

const removalIds = computed(() => {
  const ids = props.action.arguments?.removal_ids;
  return Array.isArray(ids) ? ids : [];
});

const dismissedIds = ref(new Set());
const dismissedRemovalIds = ref(new Set());

const visibleSuggestionIds = computed(() =>
  suggestionIds.value.filter((id) => !dismissedIds.value.has(id)),
);
const visibleRemovalIds = computed(() =>
  removalIds.value.filter((id) => !dismissedRemovalIds.value.has(id)),
);

const decline = declineAccountPlaceSuggestion();
const declineRemoval = declineAccountGeofenceRemovalSuggestion();

function onDismissSuggestion(id) {
  dismissedIds.value = new Set([...dismissedIds.value, id]);
  const accountId = store.getters['auth/accountId'];
  if (accountId == null) return;
  decline.mutate(
    { accountId, suggestionId: id },
    {
      onError: () => {
        const next = new Set(dismissedIds.value);
        next.delete(id);
        dismissedIds.value = next;
        toast.error('Could not dismiss suggestion. Please try again.');
      },
    },
  );
}

function onDismissRemoval(id) {
  dismissedRemovalIds.value = new Set([...dismissedRemovalIds.value, id]);
  const accountId = store.getters['auth/accountId'];
  if (accountId == null) return;
  declineRemoval.mutate(
    { accountId, suggestionId: id },
    {
      onError: () => {
        const next = new Set(dismissedRemovalIds.value);
        next.delete(id);
        dismissedRemovalIds.value = next;
        toast.error('Could not dismiss suggestion. Please try again.');
      },
    },
  );
}

const isCommandButton = computed(() => {
  return (
    [
      'ui_bulk_update_entities',
      'ui_revertable_bulk_update_entities',
      'ui_revertable_bulk_append_entities',
      'ui_revertable_manage_containers',
      'ui_revertable_manage_place_types',
      'ui_revertable_create_geofences',
      'ui_revertable_update_geofences',
      'ui_revertable_close_shipment_exceptions',
      'ui_bulk_reopen_shipment_exceptions',
    ].indexOf(props.action.name) > -1
  );
});

const label = computed(() => {
  return props.action.arguments?.button_text || props.action.name;
});

const handleAction = () => {
  if (
    [
      'ui_update_map',
      'ui_add_polygon',
      'ui_filter_devices',
      'ui_create_file',
    ].indexOf(props.action.name) > -1
  ) {
    events.trigger(props.action.name, props.action.arguments);
  } else {
    toast.error(`The ${props.action.name} is not yet supported.`);
  }
};
</script>
